export const DEFAULT_FRAGMENTS = [
  {
    label: "Objectives",
    text: "Start with clear learning objectives (3–5 measurable objectives). Each objective should be crisp containing max five words.",
    cssClass: "obj-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Prerequisites",
    text: "Include a brief pre-requisite knowledge section. This section should contain up to three pre-requisites. Each prerequisite should be crisp containing max five words.",
    cssClass: "preq-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Structure",
    text: "Have structured sections of approximately 4000 words each. Give deep insights of the topic instead of peripheral explanation. Ensure the explanation logically flows and progressively deepens understanding.",
    cssClass: "struct-card",
    multiline: true,
    rows: 4,
    scope: "section"
  },
  {
    label: "Examples",
    text: "Provide concise, clear examples illustrating theoretical points in each section.",
    cssClass: "exampl-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Did You Know",
    text: "Use highlighted \"Did You Know?\" sidebars with historical or practical insights in each section.",
    cssClass: "dyk-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Summary",
    text: "Summarize key points at the end of each major section.",
    cssClass: "sum-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Reflection",
    text: "End with reflective questions encouraging critical thinking in each section.",
    cssClass: "refl-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Glossary",
    text: "Include a short integrated glossary of key terms introduced in the lecture.",
    cssClass: "gloss-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

export const NGSS_FRAGMENTS = [
  {
    label: "Phenomenon",
    text: "Present a real-world phenomenon (≤20 words) that drives student curiosity and anchors all learning activities.",
    cssClass: "pheno-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Performance Expectations",
    text: "List 1–2 NGSS PE codes (e.g., HS-PS3-2) with condensed wording (≤25 words each).",
    cssClass: "pe-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Disciplinary Core Ideas",
    text: "State the relevant DCIs and explain (1–2 sentences) how each surfaces in the lesson.",
    cssClass: "dci-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Science & Engineering Practices",
    text: "Describe the SEP(s) learners will engage in (e.g., modeling, data analysis) and where they occur.",
    cssClass: "sep-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Crosscutting Concepts",
    text: "Identify at least one CCC (e.g., Energy & Matter) and tie it explicitly to the phenomenon.",
    cssClass: "ccc-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Inquiry Activity Plan",
    text: "Outline an investigation or design task: materials, ≤5 steps, data collection, and safety notes.",
    cssClass: "inquiry-card",
    multiline: true,
    rows: 3,
    scope: "lesson"
  },
  {
    label: "Formative Assessment",
    text: "Create 2–3 prompts or performance tasks aligned to the PE(s) for quick feedback.",
    cssClass: "assess-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Reflection & Connection",
    text: "Provide reflection questions linking new understanding back to the phenomenon and everyday contexts.",
    cssClass: "reflect-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

export const ISTE_FRAGMENTS = [
  {
    label: "Empowered Learner Goals",
    text: "State 2 ISTE Learner goals students will meet (≤25 words each).",
    cssClass: "emp-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Digital Citizenship",
    text: "Highlight one ethical/safe technology behavior that is explicitly practiced in this lesson.",
    cssClass: "digcit-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Computational Thinking Task",
    text: "Describe an activity where students decompose a problem, recognize patterns, or create a simple algorithm.",
    cssClass: "ct-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Collaboration Activity",
    text: "Plan a collaborative task using digital tools (shared doc, coding repo, virtual whiteboard, etc.).",
    cssClass: "collab-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Creative Communication",
    text: "Specify how students will convey ideas with digital media. Insert a [Simulation Placeholder] block where the final communication artifact will go (infographic, animation).",
    cssClass: "comm-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Technology Integration",
    text: "Detail the main digital tool(s) used (e.g., JupyterLab, CAD) and why they fit the learning goal.",
    cssClass: "tech-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Simulation Placeholder",
    text: "Describe an interactive visual supporting the CT task. Include goal, inputs, outputs, logic.",
    cssClass: "sim-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Assessment of Tech Skills",
    text: "Outline a rubric or criteria for evaluating students’ effective and ethical tech use.",
    cssClass: "assess-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Reflection",
    text: "Pose reflection prompts on tool choice, collaboration process, and digital-ethics takeaways.",
    cssClass: "reflect-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

export const CCSS_M_FRAGMENTS = [
  {
    label: "Learning Objectives",
    text: "Write 2–3 CCSS-Math objectives with standard codes (e.g., HSF-IF.B.4), ≤20 words each.",
    cssClass: "obj-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Prerequisite Knowledge",
    text: "List up to three prerequisite skills (≤5 words each).",
    cssClass: "preq-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Concept Development Sequence",
    text: "Outline a coherent progression: concrete → pictorial → abstract. Use ~3 sections (≤400 words each).",
    cssClass: "struct-card",
    multiline: true,
    rows: 4,
    scope: "lesson"
  },
  {
    label: "Mathematical Practices",
    text: "Indicate which SMPs (MP1–MP8) are emphasized and how students will enact them.",
    cssClass: "smp-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Modeling Task",
    text: "Describe a real-world modeling problem; give data context and guiding questions.",
    cssClass: "model-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Worked Examples",
    text: "Provide 1–2 solved examples illustrating key reasoning steps.",
    cssClass: "example-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Interactive Visualization",
    text: "Describe a dynamic visual (graph, slider demo) that illustrates the concept.",
    cssClass: "vis-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Practice Problems",
    text: "List 3 scaffolded problems with brief solutions or hints.",
    cssClass: "practice-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Exit Ticket",
    text: "Craft a short formative assessment checking mastery of objectives.",
    cssClass: "exit-card",
    multiline: true,
    rows: 1,
    scope: "section"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];


export const ABET_FRAGMENTS = [
  /* Student Outcome code */ {
    label: "ABET Outcome Mapping",
    text: "List which ABET outcomes (1–7) this lesson supports and state them in ≤20 words each.",
    cssClass: "abet-out-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  /* Learning objectives */ {
    label: "Learning Objectives",
    text: "Write 2–3 measurable objectives tied to the mapped ABET outcomes (Bloom level + verb, ≤15 words).",
    cssClass: "obj-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  /* Engineering constraints */ {
    label: "Realistic Constraints",
    text: "Describe economic, environmental, social, or safety constraints applied in project/activity.",
    cssClass: "constraint-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Professional & ethics */ {
    label: "Ethics / Professionalism",
    text: "Include a scenario or discussion prompt on professional responsibility (ABET 4).",
    cssClass: "ethics-card",
    multiline: true,
    scope: "section"
  },
  /* Teamwork */ {
    label: "Team Activity Plan",
    text: "Outline how students collaborate (roles, deliverables) to meet outcome 5.",
    cssClass: "team-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Experimentation / analysis */ {
    label: "Experiment or Simulation",
    text: "Describe an experiment OR simulation where students analyze & interpret data (outcome 6).",
    cssClass: "exp-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Lifelong learning */ {
    label: "Lifelong-Learning Component",
    text: "Provide resources/tasks that encourage independent acquisition of new knowledge (outcome 7).",
    cssClass: "lifelong-card",
    multiline: true,
    scope: "lesson"
  },
  /* Assessment */ {
    label: "Assessment & Rubric",
    text: "Define performance criteria and levels (e.g., 1-4) aligned to each mapped outcome.",
    cssClass: "rubric-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  /* Continuous-improvement note */ {
    label: "Feedback & Improvement",
    text: "State how data from this lesson will inform future course revisions (surveys, performance trends).",
    cssClass: "ci-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  }
];

export const TWENTY_FIRST_CS_FRAGMENTS = [
  /* Big-4 skills overview */ {
    label: "4C Skill Focus",
    text: "Select 1–2 of the 4Cs (Critical Thinking, Creativity, Collaboration, Communication) this lesson emphasizes and explain why (≤25 words each).",
    cssClass: "4c-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Explanatory Content",
    text: "Provide a detailed explanation, background information, or a narrative for this section (500-750 words). Introduce key concepts, definitions, and provide context for the activities that follow.",
    cssClass: "explanation-card", // You would need to add CSS for this class
    multiline: true,
    rows: 6,
    scope: "section" // Placed in each section to provide context
  },
  /* Critical-thinking task */ {
    label: "Critical-Thinking Challenge",
    text: "Describe an open-ended problem that requires analysis, evaluation, or evidence-based reasoning.",
    cssClass: "ct-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Creativity boost */ {
    label: "Creativity Activity",
    text: "Outline a brainstorm/design task that encourages divergent ideas or novel products ",
    cssClass: "crea-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Collaboration plan */ {
    label: "Collaboration Structure",
    text: "Define group size, roles, digital tool(s) and deliverable for teamwork; include accountability mechanism.",
    cssClass: "collab-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },

  /* Communication artifact */ {
    label: "Communication Product",
    text: "Write a set of instructions for a student to create a communication artifact (e.g., slide deck, infographic). Specify the intended audience and key requirements for the task. The output for this field must be text only. Generate a place holder [Simulation Placeholder] that will be replaced by an artifact",
    cssClass: "comm-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Information literacy */ {
    label: "Info-Literacy Component",
    text: "Include a mini-lesson or prompt on sourcing, evaluating, and citing information (≤30 words).",
    cssClass: "info-card",
    multiline: true,
    scope: "lesson"
  },
  /* Technology fluency */ {
    label: "Tech Fluency",
    text: "Identify digital tool(s) students will master (e.g., spreadsheet modeling, CAD basics) and link to task.",
    cssClass: "tech-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  /* Life/Career skill */ {
    label: "Life & Career Skill",
    text: "Highlight one skill (initiative, adaptability, leadership, cross-cultural) and a quick reflection prompt.",
    cssClass: "life-card",
    multiline: true,
    scope: "section"
  },
  /* Assessment */ {
    label: "4C Assessment",
    text: "Provide a rubric or checklist mapping specific behaviors/artifacts to the targeted 4C skills.",
    cssClass: "assess-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  /* Reflection */ {
    label: "Metacognitive Reflection",
    text: "Pose questions that help learners evaluate how their 4C skills improved during the activity.",
    cssClass: "reflect-card",
    multiline: true,
    scope: "lesson"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

// Prompt fragments for generating a full-length, friendly STEM lesson
export const JUNIOR_LESSON_FRAGMENTS = [  
  /*  Section building blocks  */
  {
    label: "Learning Objectives",
    text: "Each main section starts with 2-3 kid-friendly objectives (≤ 10 words each).",
    cssClass: "obj-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Engaging Intro",
    text: "Open with real-world examples to hook curiosity before the theory.",
    cssClass: "intro-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Section Structure",
    text: "Break content into short paragraphs under descriptive sub-headings (3-5 per section). Allocate around 2000 words for the section content",
    cssClass: "struct-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Activity Blocks",
    text: "Add step-by-step projects, experiments, or code challenges kids can follow.",
    cssClass: "activity-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Fun Fact Sidebars",
    text: "Insert quick call-outs with surprising facts, historical trivia, or cool applications.",
    cssClass: "funfact-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Reflection & Glossary",
    text: "End each section with reflective questions and a glossary of key terms in simple language.",
    cssClass: "reflect-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

export const SENIOR_LESSON_FRAGMENTS = [
  {
    label: "Learning Objectives",
    text: "Start every section with 2–3 simple, kid-friendly objectives (max 10 words each).",
    cssClass: "obj-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Engaging Intro",
    text: "Open with real-world examples that hook curiosity before diving into theory.",
    cssClass: "intro-card",
    multiline: true,
    scope: "section"
  },
  {
    label: "Section Structure",
    text: "Write short, well-organized paragraphs under descriptive sub-headings. Aim for 3–5 sub-sections per main section. Allocate around 2000 words for the section content",
    cssClass: "struct-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Activity Blocks",
    text: "Insert hands-on projects, experiments, or code challenges with step-by-step instructions.",
    cssClass: "activity-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Insight Sidebars",
    text: "Add brief callouts for pro tips, best practices, or common pitfalls.",
    cssClass: "sidebar-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Reflection & Glossary",
    text: "Close each section with reflective questions and a glossary of key terms explained simply.",
    cssClass: "reflect-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];

export const UNDERGRADUAT_LECTURE_FRAGMENTS = [
  /* ---------- Metadata ---------- */
  {
    label: "Learning Objectives",
    text: "List 3–5 measurable objectives per lesson using action verbs (e.g., *explain, apply, evaluate*).",
    cssClass: "obj-card",
    multiline: true,
    rows: 3,
    scope: "lesson"
  },
  {
    label: "Prerequisite Knowledge",
    text: "Briefly describe prior concepts students should know (≤ 3 bullet points).",
    cssClass: "preq-card",
    multiline: true,
    rows: 2,
    scope: "lesson"
  },
  {
    label: "Section Structure",
    text: "Organize each section with descriptive sub-headings, smooth transitions, and a logical flow. Allocate around 2000 words for the section content",
    cssClass: "struct-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Example Blocks",
    text: "Provide concise, clear worked examples or mini-case studies illustrating theory.",
    cssClass: "example-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "\"Did You Know?\" Sidebars",
    text: "Insert short call-outs with historical notes or practical insights.",
    cssClass: "sidebar-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Summary",
    text: "End each major section with 3–5 bullet points highlighting key takeaways.",
    cssClass: "summary-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Reflective Questions",
    text: "Pose 2–3 critical-thinking questions that connect theory to practice.",
    cssClass: "reflect-card",
    multiline: true,
    rows: 2,
    scope: "section"
  },
  {
    label: "Integrated Glossary",
    text: "Define all new technical terms (≤ 25 words each) immediately after first use or in a short glossary block.",
    cssClass: "reflect-card",
    multiline: true,
    rows: 3,
    scope: "section"
  },
  {
    label: "Citations",
    text: "Include in-text citations and a references list at the end of the lesson.",
    cssClass: "cite-card",
    multiline: true,
    scope: "lesson"
  }
];
